import { PageHero } from '@components/common';
import { getServiceCard } from '@data/serviceCards';
import {
  Presentation,
  TrendingUp,
  Shield,
  CheckCircle,
  ArrowRight,
  Users,
  FileText,
  PieChart,
  Target,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { CALENDLY_URL } from '@constants';

const offerings = [
  {
    icon: Presentation,
    title: 'Board Presentation Development',
    description:
      'Clear, compelling board presentations that translate technical risks into business terms directors understand.',
  },
  {
    icon: PieChart,
    title: 'Executive Dashboards',
    description:
      'Real-time security metrics and KPIs that give leadership visibility into security posture at a glance.',
  },
  {
    icon: TrendingUp,
    title: 'Investment Business Cases',
    description:
      'Data-driven business cases for security investments that demonstrate ROI and risk reduction.',
  },
  {
    icon: FileText,
    title: 'Risk Reporting',
    description:
      'Regular risk reports that keep the board informed of the evolving threat landscape and your organisation.',
  },
];

const whenYouNeedIt = [
  'Presenting to the board for the first time',
  'Need to justify security investment requests',
  'Board wants better visibility into cyber risks',
  'Struggling to communicate technical topics to non-technical directors',
  'Preparing for audit or risk committee meetings',
  'Need to report on security incidents or breaches',
  'Want to improve executive communication skills',
];

const reportingElements = [
  {
    icon: Target,
    title: 'Risk-Based Language',
    description:
      'Frame security in terms of business risk, financial impact, and strategic implications.',
  },
  {
    icon: TrendingUp,
    title: 'Trend Analysis',
    description: 'Show progress over time with clear metrics that demonstrate improvement.',
  },
  {
    icon: Shield,
    title: 'Contextual Benchmarking',
    description: 'Compare your posture to industry peers and accepted standards.',
  },
  {
    icon: Users,
    title: 'Clear Recommendations',
    description: 'Provide specific, actionable recommendations with business rationale.',
  },
];

const reportTypes = [
  {
    title: 'Monthly Security Dashboard',
    audience: 'CEO, CFO',
    content: [
      'Key metrics summary',
      'Incidents and status',
      'Risk trend indicators',
      'Top priorities',
    ],
  },
  {
    title: 'Quarterly Board Report',
    audience: 'Board of Directors',
    content: [
      'Strategic security update',
      'Risk register summary',
      'Programme progress',
      'Investment recommendations',
    ],
  },
  {
    title: 'Incident Briefing',
    audience: 'Crisis committee',
    content: [
      'Incident summary',
      'Business impact assessment',
      'Response actions taken',
      'Next steps',
    ],
  },
  {
    title: 'Investment Proposal',
    audience: 'Executive team',
    content: [
      'Risk being addressed',
      'Solution options',
      'Cost-benefit analysis',
      'Recommended approach',
    ],
  },
];

export default function BoardReportingPage() {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Board Reporting & Executive Communication',
    description: 'Translate complex security topics into clear, actionable business intelligence',
    url: 'https://thecisonetwork.com/services/board-reporting',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <PageHero
        title="Board Reporting & Executive Communication"
        subtitle="Translate complex security topics into clear, actionable business intelligence that boards understand and act upon"
        eyebrow="Our Services"
        backgroundImage={getServiceCard('board-reporting')?.image}
        backgroundAlt={getServiceCard('board-reporting')?.imageAlt}
        seoTitle="Cybersecurity Board Reporting | The CISO Network"
        seoDescription="Expert board reporting and executive communication services. Translate technical security risks into business language that drives informed decision-making."
      />

      {/* Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Boards Do Not Want Technical Details
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  One of the biggest challenges in cybersecurity is the communication gap between
                  security teams and the board. Security professionals speak in technical terms,
                  vulnerabilities, and controls - boards want to understand{' '}
                  <Link to="/services/risk-assessment" className="text-secondary hover:underline">
                    risk
                  </Link>
                  , financial impact, and strategic implications.
                </p>
                <p>
                  We help you bridge this gap. Our fractional CISOs are experienced at presenting to
                  boards and executive teams, translating complex security topics into clear,
                  actionable business intelligence. We help you tell the story that gets attention
                  and drives decisions.
                </p>
                <p>
                  Whether you need help preparing for your first board presentation, developing
                  ongoing reporting frameworks, or building business cases for{' '}
                  <Link to="/services/security-strategy" className="text-secondary hover:underline">
                    security investment
                  </Link>
                  , we provide the expertise to communicate effectively at the highest levels.
                </p>
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <div className="flex items-center gap-4 mb-6 justify-center">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center">
                  <Users className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground">
                  When You Need This
                </h3>
              </div>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {whenYouNeedIt.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <CheckCircle className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Board Reporting Services
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Everything you need to communicate security effectively at the executive and board
              level
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offerings.map((offering, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <div className="h-12 w-12 rounded-xl bg-teal flex items-center justify-center mb-6">
                  <offering.icon className="h-6 w-6 text-accent-foreground" />
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  {offering.title}
                </h3>
                <p className="text-muted-foreground">{offering.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Elements */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Effective Board Reporting
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Four key elements that transform technical security updates into compelling board
              communications
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {reportingElements.map((element, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-4">
                  <element.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                  {element.title}
                </h3>
                <p className="text-muted-foreground text-sm">{element.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Report Types */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Reporting Frameworks We Develop
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Tailored reporting for different audiences and purposes
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {reportTypes.map((report, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-border">
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                  {report.title}
                </h3>
                <p className="text-sm text-teal font-medium mb-4">Audience: {report.audience}</p>
                <ul className="space-y-2">
                  {report.content.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-muted-foreground">
                      <div className="w-1 h-1 rounded-full bg-teal" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Common Board Reporting Mistakes
              </h2>
              <div className="space-y-4">
                {[
                  {
                    mistake: 'Too much technical detail',
                    solution: 'Focus on business impact and risk, not technical implementation',
                  },
                  {
                    mistake: 'No clear ask or recommendation',
                    solution: 'Always conclude with specific decisions needed from the board',
                  },
                  {
                    mistake: 'Ignoring the good news',
                    solution: 'Balance risks with progress and improvements demonstrated',
                  },
                  {
                    mistake: 'Inconsistent metrics',
                    solution: 'Track consistent KPIs that show trends over time',
                  },
                  {
                    mistake: 'Surprising the board',
                    solution: 'No surprises - brief key directors before the meeting',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-red-600 font-bold text-sm">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-1">
                        {item.mistake}
                      </h4>
                      <p className="text-muted-foreground text-sm">{item.solution}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-background rounded-2xl p-8 border border-border">
              <h3 className="text-xl font-heading font-bold text-foreground mb-6 text-center">
                What Boards Want to Know
              </h3>
              <div className="flex justify-center">
                <ul className="space-y-4 inline-block text-left">
                  {[
                    'What are our top cyber risks right now?',
                    'How does our security compare to peers?',
                    'Are we spending the right amount on security?',
                    'What progress have we made since last quarter?',
                    'Do we have any incidents we need to know about?',
                    'Are we compliant with relevant regulations?',
                    'What decisions do we need to make?',
                  ].map((question, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted-foreground">
                      <div className="h-5 w-5 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-teal text-xs">Q</span>
                      </div>
                      <span>{question}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Communicate Security Effectively
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your board reporting needs and how our{' '}
            <Link to="/what-is-a-fractional-ciso" className="text-teal hover:underline">
              fractional CISOs
            </Link>{' '}
            can help you communicate with confidence.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Discovery Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
